import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import { WishlistProvider } from "./context/WishlistContext";
import { SiteSettingsProvider } from "./context/SiteSettingsContext";
import ScrollToTop from "./components/ScrollToTop";
import VibrantHome from "./pages/VibrantHome";
import SuperShopPage from "./pages/SuperShopPage";
import BrandsPage from "./pages/BrandsPage";
import ProductPage from "./pages/ProductPage";
import WishlistPage from "./pages/WishlistPage";
import CartPage from "./pages/CartPage";
import PremiumCheckoutPage from "./pages/PremiumCheckoutPage";
import CheckoutSuccessPage from "./pages/CheckoutSuccessPage";
import AccountPage from "./pages/AccountPage";
import BlogPage from "./pages/BlogPage";
import BlogPostPage from "./pages/BlogPostPage";
import ContactPage from "./pages/ContactPage";
import ShippingPolicy from "./pages/ShippingPolicy";
import ReturnPolicy from "./pages/ReturnPolicy";
import TermsConditions from "./pages/TermsConditions";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import AuthCallback from "./pages/AuthCallback";
import AboutPage from "./pages/AboutPage";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminProducts from "./pages/admin/AdminProducts";
import AdminProductCreate from "./pages/admin/AdminProductCreate";
import AdminProductEdit from "./pages/admin/AdminProductEdit";
import AdminBulkUpload from "./pages/admin/AdminBulkUpload";
import AdminOrders from "./pages/admin/AdminOrders";
import AdminCustomers from "./pages/admin/AdminCustomers";
import AdminBrands from "./pages/admin/AdminBrands";
import AdminCategories from "./pages/admin/AdminCategories";
import AdminBlog from "./pages/admin/AdminBlog";
import AdminPromoCodes from "./pages/admin/AdminPromoCodes";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminHomepage from "./pages/admin/AdminHomepage";
import AdminTeam from "./pages/admin/AdminTeam";
import AdminLogin from "./pages/admin/AdminLogin";
import ProtectedAdminRoute from "./components/admin/ProtectedAdminRoute";
import SerenityHeader from "./components/SerenityHeader";
import CompleteFooter from "./components/CompleteFooter";
import { Toaster } from "./components/ui/sonner";

// Component to handle Google OAuth callback - checks for session_id in URL hash
const AppRouter = () => {
  const location = useLocation();
  
  // CRITICAL: Check for session_id synchronously during render (before ProtectedRoute runs)
  // This prevents race conditions with auth state
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  // Handle /auth/callback route with query params (Google OAuth)
  if (location.pathname === '/auth/callback') {
    return <AuthCallback />;
  }
  
  return (
    <>
      <ScrollToTop />
      <Routes>
            {/* Admin Login - Public */}
            <Route path="/admin/login" element={<AdminLogin />} />
            
            {/* Admin Routes - Protected */}
            <Route path="/admin" element={<ProtectedAdminRoute><AdminDashboard /></ProtectedAdminRoute>} />
            <Route path="/admin/products" element={<ProtectedAdminRoute><AdminProducts /></ProtectedAdminRoute>} />
            <Route path="/admin/products/create" element={<ProtectedAdminRoute><AdminProductCreate /></ProtectedAdminRoute>} />
            <Route path="/admin/products/edit/:id" element={<ProtectedAdminRoute><AdminProductEdit /></ProtectedAdminRoute>} />
            <Route path="/admin/bulk-upload" element={<ProtectedAdminRoute><AdminBulkUpload /></ProtectedAdminRoute>} />
            <Route path="/admin/orders" element={<ProtectedAdminRoute><AdminOrders /></ProtectedAdminRoute>} />
            <Route path="/admin/customers" element={<ProtectedAdminRoute><AdminCustomers /></ProtectedAdminRoute>} />
            <Route path="/admin/brands" element={<ProtectedAdminRoute><AdminBrands /></ProtectedAdminRoute>} />
            <Route path="/admin/categories" element={<ProtectedAdminRoute><AdminCategories /></ProtectedAdminRoute>} />
            <Route path="/admin/blog" element={<ProtectedAdminRoute><AdminBlog /></ProtectedAdminRoute>} />
            <Route path="/admin/promo-codes" element={<ProtectedAdminRoute><AdminPromoCodes /></ProtectedAdminRoute>} />
            <Route path="/admin/homepage" element={<ProtectedAdminRoute><AdminHomepage /></ProtectedAdminRoute>} />
            <Route path="/admin/team" element={<ProtectedAdminRoute><AdminTeam /></ProtectedAdminRoute>} />
            <Route path="/admin/settings" element={<ProtectedAdminRoute><AdminSettings /></ProtectedAdminRoute>} />
            
            {/* Auth Routes - Public */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            
            {/* Public Routes */}
            <Route path="/*" element={
              <>
                <SerenityHeader />
                <main>
                  <Routes>
                    <Route path="/" element={<VibrantHome />} />
                    <Route path="/shop" element={<SuperShopPage />} />
                    <Route path="/shop/category/:slug" element={<SuperShopPage />} />
                    <Route path="/shop/product/:slug" element={<ProductPage />} />
                    <Route path="/product/:slug" element={<ProductPage />} />
                    <Route path="/brands" element={<BrandsPage />} />
                    <Route path="/collections/:slug" element={<SuperShopPage />} />
                    <Route path="/blog" element={<BlogPage />} />
                    <Route path="/blog/:slug" element={<BlogPostPage />} />
                    <Route path="/contact" element={<ContactPage />} />
                    <Route path="/about" element={<AboutPage />} />
                    <Route path="/shipping-policy" element={<ShippingPolicy />} />
                    <Route path="/return-policy" element={<ReturnPolicy />} />
                    <Route path="/terms" element={<TermsConditions />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                    <Route path="/wishlist" element={<WishlistPage />} />
                    <Route path="/cart" element={<CartPage />} />
                    <Route path="/account" element={<AccountPage />} />
                  </Routes>
                </main>
                <CompleteFooter />
              </>
            } />
            
            {/* Checkout Routes - Standalone without header/footer */}
            <Route path="/checkout" element={<PremiumCheckoutPage />} />
            <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
          </Routes>
    </>
  );
};

function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <WishlistProvider>
          <SiteSettingsProvider>
            <div className="App bg-bg-main min-h-screen">
              <BrowserRouter>
                <AppRouter />
                <Toaster />
              </BrowserRouter>
            </div>
          </SiteSettingsProvider>
        </WishlistProvider>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
